/**
 * Locale-aware drop-in replacements for react-router-dom's Link, NavLink and
 * Navigate. String `to` values that are internal paths are prefixed with /es
 * when the active locale is Spanish. Pass `locale` to force a target locale.
 */
import { forwardRef } from "react";
import {
  Link as RRLink,
  NavLink as RRNavLink,
  Navigate as RRNavigate,
  type LinkProps,
  type NavLinkProps,
  type NavigateProps,
  type To,
} from "react-router-dom";
import { useLocale } from "./LocaleContext";
import { localizePath, type Locale } from "./locale";

function loc(to: To, locale: Locale): To {
  if (typeof to === "string") return localizePath(to, locale);
  if (to.pathname) return { ...to, pathname: localizePath(to.pathname, locale) };
  return to;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps & { locale?: Locale }>(
  function Link({ to, locale, ...rest }, ref) {
    const ctx = useLocale();
    return <RRLink ref={ref} to={loc(to, locale ?? ctx.locale)} {...rest} />;
  },
);

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps & { locale?: Locale }>(
  function NavLink({ to, locale, ...rest }, ref) {
    const ctx = useLocale();
    return <RRNavLink ref={ref} to={loc(to, locale ?? ctx.locale)} {...rest} />;
  },
);

export function Navigate({ to, ...rest }: NavigateProps) {
  const { locale } = useLocale();
  return <RRNavigate to={loc(to, locale)} {...rest} />;
}
